// src/admin/FormEditor/RichTextEditor.jsx
// Rich text editor component with source code view
// Features:
// - Visual/Source mode toggle
// - Resizable editor height
// - Persistent height storage
// - Different toolbar options based on field type
// - Multi-language support

// React and core dependencies
import { ChevronRight, Code, Edit2, GripVertical, Info } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

// Third-party libraries
import Delta from "quill-delta";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Initialize Quill and register custom format
const Quill = ReactQuill.Quill;
const Parchment = Quill.import("parchment");

// Register hanging indent format
const HangingIndentClass = new Parchment.Attributor.Class(
  "hanging",
  "hanging-indent",
  {
    scope: Parchment.Scope.BLOCK,
    whitelist: ["true"],
  }
);
Quill.register(HangingIndentClass, true);

export const RichTextEditor = memo(
  ({
    value = "",
    onChange,
    language,
    fieldName,
    collapsible = false, // Whether the editor can be collapsed
    defaultCollapsed = false, // Initial collapse state
    title = "Rich Text Editor", // Title for the collapse header
    forceReset = false,
  }) => {
    const storageKey = useMemo(() => {
      return `editor-collapsed-${fieldName}-${language}-${title
        .toLowerCase()
        .replace(/\s+/g, "-")}`;
    }, [fieldName, language, title]);

    // Editor state
    const [activeTab, setActiveTab] = useState("visual");
    const [isFormatted, setIsFormatted] = useState(true);
    const [showFormatInfo, setShowFormatInfo] = useState(false);

    // Reference to the editor
    const quillRef = useRef(null);

    // Height management
    const [height, setHeight] = useState(() => {
      const savedHeight = localStorage.getItem(
        `${fieldName}-${language}-height`
      );
      return savedHeight
        ? parseInt(savedHeight, 10)
        : fieldName === "details"
        ? 300
        : 150;
    });

    // Update the initial state of isCollapsed
    const [isCollapsed, setIsCollapsed] = useState(() => {
      if (!collapsible) return false;

      // Force the default state on first render
      const forceDefault = true; // You can make this a prop if needed
      if (forceDefault) {
        localStorage.removeItem(storageKey);
        return defaultCollapsed;
      }

      const saved = localStorage.getItem(storageKey);
      return saved !== null ? JSON.parse(saved) : defaultCollapsed;
    });

    // Update localStorage when collapse state changes
    useEffect(() => {
      if (collapsible) {
        localStorage.setItem(storageKey, JSON.stringify(isCollapsed));
      }
    }, [isCollapsed, collapsible, storageKey]);

    const [isDragging, setIsDragging] = useState(false);

    // Width management
    const [width, setWidth] = useState(() => {
      const savedWidth = localStorage.getItem(`${fieldName}-${language}-width`);
      return savedWidth ? parseInt(savedWidth, 10) : 800;
    });
    const [isResizingWidth, setIsResizingWidth] = useState(false);

    // Refs
    const containerRef = useRef(null);
    const initialY = useRef(0);
    const initialHeight = useRef(0);
    const initialX = useRef(0);
    const initialWidth = useRef(0);
    const throttleTimerRef = useRef(null);

    const modules = useMemo(() => {
      return {
        toolbar: {
          container: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ indent: "-1" }, { indent: "+1" }],
            ["link", "image"],
            [{ align: [] }],
            ["clean"],
            ["hanging"],
          ],
          handlers: {
            hanging: function (value) {
              const format = this.quill.getFormat();
              this.quill.format("hanging", !format.hanging);
            },
          },
        },
        clipboard: {
          matchVisual: false,
          // Add more sophisticated matchers
          matchers: [
            [
              "span",
              (node, delta) => {
                // Check for spaces in text content
                const text = node.textContent;
                if (text.match(/\s\s+/)) {
                  // Convert all multiple spaces to &nbsp;
                  const newText = text.replace(/\s\s+/g, (match) => {
                    return "\u00A0".repeat(match.length);
                  });
                  return new Delta().insert(newText);
                }
                return delta;
              },
            ],
            [
              "text",
              (node, delta) => {
                // Handle text nodes directly
                const text = node.data;
                if (text.match(/\s\s+/)) {
                  const newText = text.replace(/\s\s+/g, (match) => {
                    return "\u00A0".repeat(match.length);
                  });
                  return new Delta().insert(newText);
                }
                return delta;
              },
            ],
          ],
        },
      };
    }, [fieldName]);

    // Height resize handlers
    const handleHeightResizeStart = (e) => {
      setIsDragging(true);
      initialY.current = e.clientY;
      initialHeight.current = height;
    };

    // Width resize handlers
    const handleWidthResizeStart = (e) => {
      setIsResizingWidth(true);
      initialX.current = e.clientX;
      initialWidth.current = width;
    };

    // Simple tab switching without modifying the content
    const handleTabSwitch = (newTab) => {
      setActiveTab(newTab);
    };

    // Format HTML for better readability in source view
    const formatHTML = (html) => {
      if (!isFormatted || !html) return html;

      let formatted = "";
      let indent = 0;
      const INDENT_SIZE = 2;

      // Split on tags, but keep the tags in the result
      const tokens = html.split(/(<\/?[^>]+>)/);

      // List of tags that don't need a line break
      const inlineTags = new Set(["strong", "em", "u", "span", "a", "br"]);

      tokens.forEach((token) => {
        if (!token.trim()) return; // Skip empty tokens

        // Check if it's a tag
        if (token.startsWith("<")) {
          // Check if it's a closing tag
          if (token.startsWith("</")) {
            const tagName = token.match(/<\/([^>]+)>/)?.[1]?.toLowerCase();
            if (!inlineTags.has(tagName)) {
              indent -= INDENT_SIZE;
              formatted += "\n" + " ".repeat(indent);
            }
          }

          formatted += token;

          // If it's an opening tag (not self-closing)
          if (!token.startsWith("</") && !token.endsWith("/>")) {
            const tagName = token.match(/<([^\s>]+)/)?.[1]?.toLowerCase();
            if (!inlineTags.has(tagName)) {
              indent += 2;
              formatted += "\n" + " ".repeat(indent);
            }
          }
        } else {
          // It's content
          formatted += token;
        }
      });

      return formatted;
    };

    // Throttled change handler to prevent cursor issues and frequent JSON updates
    const handleChange = useCallback(
      (newValue) => {
        // Store the current cursor position if available
        let cursorPosition = null;
        if (quillRef.current) {
          const editor = quillRef.current.getEditor();
          cursorPosition = editor.getSelection();
        }

        // Use throttling to avoid too frequent updates to parent component
        if (throttleTimerRef.current) {
          clearTimeout(throttleTimerRef.current);
        }

        // Only propagate changes after a small delay
        throttleTimerRef.current = setTimeout(() => {
          onChange(newValue);

          // Restore cursor position after a small delay
          if (cursorPosition && quillRef.current) {
            setTimeout(() => {
              const editor = quillRef.current.getEditor();
              editor.setSelection(cursorPosition);
            }, 0);
          }
        }, 150);
      },
      [onChange]
    );

    // for height resize
    useEffect(() => {
      const handleMouseMove = (e) => {
        if (isDragging && containerRef.current) {
          const deltaY = e.clientY - initialY.current;
          const newHeight = Math.max(150, initialHeight.current + deltaY);
          setHeight(newHeight);
        }
      };

      const handleMouseUp = () => {
        if (isDragging) {
          setIsDragging(false);
          // Only save to localStorage when dragging ends
          localStorage.setItem(`${fieldName}-${language}-height`, height);
        }
      };

      if (isDragging) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
      }

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }, [isDragging, fieldName, language, height]);

    // for width resize
    useEffect(() => {
      const handleMouseMove = (e) => {
        if (isResizingWidth && containerRef.current) {
          const deltaX = e.clientX - initialX.current;
          const newWidth = Math.max(400, initialWidth.current + deltaX);
          setWidth(newWidth);
        }
      };

      const handleMouseUp = () => {
        if (isResizingWidth) {
          setIsResizingWidth(false);
          localStorage.setItem(`${fieldName}-${language}-width`, width);
        }
      };

      if (isResizingWidth) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
      }

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }, [isResizingWidth, fieldName, language, width]);

    const formats = [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "color",
      "background",
      "align",
      "link",
      "indent",
      "hanging",
    ];

    // Setup the hanging indent button with proper SVG namespace
    useEffect(() => {
      // Only run this effect when in visual mode and the editor is mounted
      if (activeTab !== "visual" || !quillRef.current) {
        return;
      }

      // Get the toolbar element from this specific editor instance
      const editorElement = quillRef.current.getEditor().container;
      const toolbar = editorElement
        .closest(".rich-text-editor")
        ?.querySelector(".ql-toolbar");

      if (!toolbar) {
        console.warn("Could not find toolbar element for editor");
        return;
      }

      // Find the hanging button in this specific toolbar
      const hangingButton = toolbar.querySelector(".ql-hanging");

      if (!hangingButton) {
        console.warn("Could not find hanging button in toolbar");
        return;
      }

      // Skip if button already has content (has SVG)
      if (hangingButton.querySelector("svg")) {
        return;
      }

      // Clear existing content
      hangingButton.innerHTML = "";

      // Create SVG icon with proper namespace
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("width", "18");
      svg.setAttribute("height", "18");
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.setAttribute("fill", "none");
      svg.setAttribute("stroke", "currentColor");
      svg.setAttribute("stroke-width", "2");
      svg.setAttribute("stroke-linecap", "round");
      svg.setAttribute("stroke-linejoin", "round");

      // Create the lines for the icon
      const line1 = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "line"
      );
      line1.setAttribute("x1", "4");
      line1.setAttribute("y1", "6");
      line1.setAttribute("x2", "20");
      line1.setAttribute("y2", "6");

      const line2 = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "line"
      );
      line2.setAttribute("x1", "8");
      line2.setAttribute("y1", "12");
      line2.setAttribute("x2", "20");
      line2.setAttribute("y2", "12");

      const line3 = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "line"
      );
      line3.setAttribute("x1", "8");
      line3.setAttribute("y1", "18");
      line3.setAttribute("x2", "20");
      line3.setAttribute("y2", "18");

      // Append all elements
      svg.appendChild(line1);
      svg.appendChild(line2);
      svg.appendChild(line3);
      hangingButton.appendChild(svg);

      console.log("Hanging button set up for editor:", fieldName, language);

      // Also set up a more aggressive retry mechanism
      const retrySetup = () => {
        const button = toolbar.querySelector(".ql-hanging:not(:has(svg))");
        if (button) {
          // Clone the SVG and append it to the button without SVG
          const newSvg = svg.cloneNode(true);
          button.innerHTML = "";
          button.appendChild(newSvg);
          console.log("Fixed hanging button in retry");
        }
      };

      // Try a few times with increasing delays
      setTimeout(retrySetup, 200);
      setTimeout(retrySetup, 500);
      setTimeout(retrySetup, 1000);
    }, [activeTab, fieldName, language, value]);

    // Clean up on unmount
    useEffect(() => {
      return () => {
        if (throttleTimerRef.current) {
          clearTimeout(throttleTimerRef.current);
        }
      };
    }, []);

    // Effect to handle force reset
    useEffect(() => {
      if (forceReset) {
        localStorage.removeItem(storageKey);
        setIsCollapsed(defaultCollapsed);
      }
    }, [forceReset, defaultCollapsed, storageKey]);

    return (
      <>
        {/* Scoped styles */}
        <style>
          {`
/* Base Editor Container */
.rich-text-editor {
  position: relative;
  max-width: 100%;
}

/* Editor Core Styles */
.rich-text-editor .ql-container {
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  border: none !important;
}

.rich-text-editor .ql-editor {
  min-height: 100px;
  resize: none !important;
}

/* Toolbar Styles */
.rich-text-editor .ql-toolbar {
  border-top: none !important;
  border-left: none !important;
  border-right: none !important;
  border-bottom: 1px solid #e5e7eb !important;
  position: sticky !important;
  top: 0;
  background: white;
  z-index: 1;
}

/* Hanging Indent Button - Override default icon */
.ql-hanging:before {
  content: none !important;
}

.ql-hanging {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 24px;
}

.ql-hanging.ql-active {
  color: #06c;
}

/* Hanging Indent Tooltip */
.ql-hanging:hover::before {
  content: 'Toggle hanging indent';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

/* Resize Handles */
.rich-text-editor .resize-handle {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 8px;
  cursor: ns-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.rich-text-editor .resize-handle:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.rich-text-editor .resize-handle.dragging {
  background-color: rgba(0, 0, 0, 0.1);
}

.width-resize-handle {
  position: absolute;
  right: -4px;
  top: 0;
  bottom: 0;
  width: 8px;
  cursor: ew-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.width-resize-handle:hover,
.width-resize-handle.dragging {
  background-color: rgba(0, 0, 0, 0.1);
}

/* Size Indicator */
.rich-text-editor .size-indicator {
  font-size: 0.75rem;
  color: #9ca3af;
  text-align: right;
  padding: 0.25rem 0.5rem;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

/* Style for the SVG inside button */
.ql-hanging svg {
  width: 18px;
  height: 18px;
  display: block;
}

/* Regular indents */
.rich-text-editor .ql-editor .ql-indent-1 { margin-left: 1rem; } /* 4px */
.rich-text-editor .ql-editor .ql-indent-2 { margin-left: 3rem; } /* 12px */
.rich-text-editor .ql-editor .ql-indent-3 { margin-left: 5rem; } /* 20px */

/* Hanging indent base style */
.rich-text-editor .ql-editor p.hanging-indent-true {
  padding-left: 2rem;  /* 8px */
  text-indent: -2rem;  /* -8px */
}

/* Hanging indent combined with regular indent */
.rich-text-editor .ql-editor p.hanging-indent-true.ql-indent-1 {
  padding-left: 2rem;
  text-indent: -2rem;
  margin-left: 1rem;
}

.rich-text-editor .ql-editor p.hanging-indent-true.ql-indent-2 {
  padding-left: 2rem;
  text-indent: -2rem;
  margin-left: 3rem;
}

.rich-text-editor .ql-editor p.hanging-indent-true.ql-indent-3 {
  padding-left: 2rem;
  text-indent: -2rem;
  margin-left: 5rem;
}

.rich-text-editor.collapsed {
  border-color: #e5e7eb;
}

.rich-text-editor.collapsed:hover {
  border-color: #d1d5db;
}

/* Align Button Styles */
.ql-align {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 24px;
}

.ql-picker.ql-align {
  width: auto;
  height: auto;
}

.ql-align.ql-active {
  color: #06c;
}

/* Align Button Tooltip */
.ql-align:hover::before {
  content: 'Align text (converts to inline styles)';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

/* Align options dropdown styles */
.ql-picker.ql-align .ql-picker-label {
  padding: 0 4px;
}

.ql-picker.ql-align .ql-picker-options {
  padding: 4px 0;
}

.ql-picker.ql-align .ql-picker-item {
  padding: 4px 8px;
}

.ql-formats .ql-align.ql-picker {
  width: 35px;
  height: 24px;
  display: inline-block;
}

.ql-align .ql-picker-label {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.ql-align .ql-picker-label svg {
  width: 18px;
  height: 18px;
  display: block;
}

/* Icons for alignment options */
.ql-align .ql-picker-item[data-value=""],
.ql-align .ql-picker-label[data-value=""] {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 18 18'%3E%3Cline x1='3' y1='6' x2='15' y2='6' stroke='currentColor' stroke-width='1'/%3E%3Cline x1='3' y1='9' x2='15' y2='9' stroke='currentColor' stroke-width='1'/%3E%3Cline x1='3' y1='12' x2='15' y2='12' stroke='currentColor' stroke-width='1'/%3E%3C/svg%3E");
  background-position: center;
  background-repeat: no-repeat;
}

.ql-align .ql-picker-item[data-value="center"],
.ql-align .ql-picker-label[data-value="center"] {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 18 18'%3E%3Cline x1='4' y1='6' x2='14' y2='6' stroke='currentColor' stroke-width='1'/%3E%3Cline x1='6' y1='9' x2='12' y2='9' stroke='currentColor' stroke-width='1'/%3E%3Cline x1='4' y1='12' x2='14' y2='12' stroke='currentColor' stroke-width='1'/%3E%3C/svg%3E");
  background-position: center;
  background-repeat: no-repeat;
}

.ql-align .ql-picker-item[data-value="right"],
.ql-align .ql-picker-label[data-value="right"] {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 18 18'%3E%3Cline x1='3' y1='6' x2='15' y2='6' stroke='currentColor' stroke-width='1'/%3E%3Cline x1='7' y1='9' x2='15' y2='9' stroke='currentColor' stroke-width='1'/%3E%3Cline x1='3' y1='12' x2='15' y2='12' stroke='currentColor' stroke-width='1'/%3E%3C/svg%3E");
  background-position: center;
  background-repeat: no-repeat;
}

.ql-align .ql-picker-item[data-value="justify"],
.ql-align .ql-picker-label[data-value="justify"] {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 18 18'%3E%3Cline x1='3' y1='6' x2='15' y2='6' stroke='currentColor' stroke-width='1'/%3E%3Cline x1='3' y1='9' x2='15' y2='9' stroke='currentColor' stroke-width='1'/%3E%3Cline x1='3' y1='12' x2='15' y2='12' stroke='currentColor' stroke-width='1'/%3E%3C/svg%3E");
  background-position: center;
  background-repeat: no-repeat;
}

/* Style for the picker options */
.ql-align .ql-picker-options {
  width: auto;
  padding: 5px 0;
  background: white;
  border: 1px solid #ccc;
  border-radius: 3px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.ql-align .ql-picker-item {
  display: block;
  width: 100%;
  height: 24px;
  padding: 5px 15px;
  cursor: pointer;
}

.ql-align .ql-picker-item:hover {
  background-color: #f3f4f6;
}

/* Tooltips for alignment items */
.ql-align .ql-picker-item[data-value=""]:hover::before {
  content: 'Left align (default)';
  position: absolute;
  top: 0;
  right: calc(100% + 5px);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

.ql-align .ql-picker-item[data-value="center"]:hover::before {
  content: 'Center align → text-align: center';
  position: absolute;
  top: 0;
  right: calc(100% + 5px);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

.ql-align .ql-picker-item[data-value="right"]:hover::before {
  content: 'Right align → text-align: right';
  position: absolute;
  top: 0;
  right: calc(100% + 5px);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

.ql-align .ql-picker-item[data-value="justify"]:hover::before {
  content: 'Justify → text-align: justify';
  position: absolute;
  top: 0;
  right: calc(100% + 5px);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

/* Format info button */
.format-info-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  cursor: pointer;
  transition: background-color 0.2s;
}

.format-info-button:hover {
  background-color: rgba(59, 130, 246, 0.2);
}

/* Format info panel */
.format-info-panel {
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  padding: 12px;
  font-size: 13px;
  line-height: 1.5;
  z-index: 20;
}

.format-info-panel h4 {
  margin-top: 0;
  margin-bottom: 8px;
  font-weight: 600;
  color: #1f2937;
}

.format-info-panel p {
  margin-top: 0;
  margin-bottom: 8px;
  color: #4b5563;
}

.format-info-panel code {
  background-color: #f3f4f6;
  padding: 2px 4px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  color: #1f2937;
}

.format-info-panel .example {
  display: block;
  margin-top: 4px;
  margin-bottom: 8px;
  padding: 6px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

/* Warning banner */
.rich-text-editor .info-banner {
  padding: 8px 12px;
  background-color: rgba(59, 130, 246, 0.1);
  border-bottom: 1px solid rgba(59, 130, 246, 0.2);
  display: flex;
  align-items: center;
  gap: 8px;
}

.rich-text-editor .info-banner p {
  margin: 0;
  font-size: 13px;
  color: #1e40af;
}

.rich-text-editor .info-banner .info-icon {
  flex-shrink: 0;
  color: #3b82f6;
}

`}
        </style>

        <div
          className={`border rounded-md overflow-hidden rich-text-editor ${
            isCollapsed ? "collapsed" : ""
          }`}
          ref={containerRef}
          style={{ width: `${width}px` }}
        >
          {/* Collapsible Header - only shown if collapsible is true */}
          {collapsible && (
            <div
              className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b cursor-pointer select-none"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              <div className="flex items-center gap-2">
                <ChevronRight
                  size={16}
                  className={`transform transition-transform ${
                    isCollapsed ? "" : "rotate-90"
                  }`}
                />
                <span className="font-medium text-sm">{title}</span>
              </div>
              {!isCollapsed && (
                <span className="text-xs text-gray-500">
                  {activeTab === "visual" ? "Visual Editor" : "Source Code"}
                </span>
              )}
            </div>
          )}

          {/* Editor Content - hidden when collapsed */}
          {!isCollapsed && (
            <>
              {/* Warning Message */}
              <div className="px-3 py-2 bg-red-50 border-b border-red-100">
                <p className="text-sm font-bold text-red-600">
                  You may need to edit the source in JSON file directly in case
                  of any issues in this RichTextEditor.
                </p>
              </div>

              {/* Format Info */}
              <div className="info-banner">
                <div className="info-icon">
                  <Info size={16} />
                </div>
                <p>
                  Text alignment classes (e.g., <code>ql-align-center</code>)
                  will be automatically converted to inline styles (e.g.,{" "}
                  <code>style="text-align: center;"</code>) when displayed in
                  the renderer.
                  <button
                    className="ml-2 text-blue-600 underline text-xs"
                    onClick={() => setShowFormatInfo(!showFormatInfo)}
                  >
                    {showFormatInfo ? "Hide details" : "Learn more"}
                  </button>
                </p>

                {showFormatInfo && (
                  <div className="format-info-panel">
                    <h4>Text Alignment Conversion</h4>
                    <p>
                      The editor uses Quill's built-in classes for text
                      alignment, but these are automatically converted to inline
                      styles when the content is loaded and displayed by the
                      renderer.
                    </p>

                    <h4>Conversion Examples:</h4>

                    <p>
                      <strong>Center alignment:</strong>
                    </p>
                    <code className="example">
                      &lt;p class="ql-align-center"&gt;Centered text&lt;/p&gt;
                      <br />
                      <br />
                      ↓ converts to ↓
                      <br />
                      <br />
                      &lt;p style="text-align: center;"&gt;Centered
                      text&lt;/p&gt;
                    </code>

                    <p>
                      <strong>Right alignment:</strong>
                    </p>
                    <code className="example">
                      &lt;p class="ql-align-right"&gt;Right aligned&lt;/p&gt;
                      <br />
                      <br />
                      ↓ converts to ↓
                      <br />
                      <br />
                      &lt;p style="text-align: right;"&gt;Right
                      aligned&lt;/p&gt;
                    </code>

                    <p>
                      <strong>Justify alignment:</strong>
                    </p>
                    <code className="example">
                      &lt;p class="ql-align-justify"&gt;Justified text&lt;/p&gt;
                      <br />
                      <br />
                      ↓ converts to ↓
                      <br />
                      <br />
                      &lt;p style="text-align: justify;"&gt;Justified
                      text&lt;/p&gt;
                    </code>

                    <p>
                      <strong>Hanging indent:</strong>
                    </p>
                    <code className="example">
                      &lt;p class="hanging-indent-true"&gt;Hanging
                      indent&lt;/p&gt;
                      <br />
                      <br />
                      ↓ converts to ↓
                      <br />
                      <br />
                      &lt;p style="padding-left: 2rem; text-indent:
                      -2rem;"&gt;Hanging indent&lt;/p&gt;
                    </code>

                    <p className="text-xs text-gray-500 mt-4">
                      This conversion ensures consistent rendering across
                      different environments.
                    </p>
                  </div>
                )}
              </div>

              {/* Tab Navigation */}
              <div className="flex border-b bg-gray-50">
                <button
                  onClick={() => handleTabSwitch("visual")}
                  className={`px-2 py-2 gap-2 flex items-center text-sm font-medium ${
                    activeTab === "visual"
                      ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Edit2 size={16} />
                  EDITOR
                </button>
                <button
                  onClick={() => handleTabSwitch("source")}
                  className={`px-2 py-2 gap-2 flex items-center text-sm font-medium ${
                    activeTab === "source"
                      ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Code size={16} />
                  SOURCE
                </button>

                {/* Add format toggle button */}
                {activeTab === "source" && (
                  <button
                    onClick={() => setIsFormatted(!isFormatted)}
                    className={`px-2 py-2 gap-2 flex items-center text-sm font-medium ml-auto
                  ${
                    isFormatted
                      ? "text-green-600 hover:text-green-700"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  >
                    {isFormatted ? "Formatted" : "Minified"}
                  </button>
                )}
              </div>

              {/* Editor Content */}
              <div className="bg-white relative">
                {activeTab === "visual" ? (
                  <div style={{ height: `${height}px` }}>
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={value}
                      onChange={handleChange}
                      modules={modules}
                      formats={formats}
                      style={{ height: `${height - 42}px` }}
                      aria-label={`Rich text editor for ${language} ${fieldName}`}
                    />
                  </div>
                ) : (
                  <textarea
                    value={formatHTML(value)}
                    readOnly
                    className="w-full p-4 font-mono text-sm bg-gray-50"
                    style={{ height: `${height}px`, resize: "none" }}
                  />
                )}

                {/* Resize Handle */}
                <div
                  className={`resize-handle ${isDragging ? "dragging" : ""}`}
                  onMouseDown={handleHeightResizeStart}
                >
                  <GripVertical size={12} className="text-gray-400" />
                </div>

                <div
                  className={`width-resize-handle ${
                    isResizingWidth ? "dragging" : ""
                  }`}
                  onMouseDown={handleWidthResizeStart}
                >
                  <GripVertical size={12} className="text-gray-400 rotate-90" />
                </div>
              </div>

              {/* Size indicator */}
              <div className="size-indicator" aria-live="polite">
                Height: {height}px
              </div>
            </>
          )}
        </div>
      </>
    );
  }
);
